/**
 * Showcase Slider — slide structure and starter content.
 *
 * A slide is always exactly two halves, in this order: the media half, which
 * the parent renders into the sliding media strip (and again into the edge
 * preview), and the panel half, which it renders into the track clipped inside
 * the stationary colour panel. render.php addresses them positionally, which
 * is why the slide locks its own inner-block list.
 *
 * The halves carry `templateLock: false` of their own: a lock is inherited by
 * inner blocks, so without it the slide's lock would reach down and leave the
 * author unable to edit anything inside either half.
 */

const CARD_PADDING = {
  top: '24px',
  right: '24px',
  bottom: '24px',
  left: '24px',
};

/*
 * Three slides. The panel colour belongs to the slide rather than to the panel
 * half, because the panel box is shared by every slide — the colour is what
 * cross-fades as the active slide changes.
 */
const SHOWCASE_SLIDES = [
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-1.webp',
    alt: 'Deck art laid out on a workbench',
    category: 'Decks',
    product: 'Turn Deck Vivid',
    price: '$309.00',
    quote: '“Every board is pressed, sanded and screened in the same workshop.”',
    author: 'Jenny Wilson',
    location: 'New Mexico',
    panelColor: '#dbe1ff',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-2.webp',
    alt: 'Rider mid-trick at the park',
    category: 'Wheels',
    product: 'Highline 54mm',
    price: '$229.00',
    quote: '“Each piece is tested for a season before it ever reaches the shop.”',
    author: 'Julie Mathers',
    location: 'Australia',
    panelColor: '#f5e2e2',
  },
  {
    url: 'https://images.wpbrandy.com/uploads/skateboards-post-img-3.webp',
    alt: 'Wheels and bearings on a shop counter',
    category: 'Bearings',
    product: 'Cross Walnut Set',
    price: '$189.00',
    quote: '“Small batches, so nothing ships until it is genuinely ready.”',
    author: 'Marcus Lee',
    location: 'Portland',
    panelColor: '#f0efe4',
  },
];

/**
 * Media half — a cover carrying the settle class, with a product card over it.
 *
 * A cover rather than an image because it clips (the settle overscans by 3%)
 * and because it is the one core block that puts content over media. The class
 * goes on the cover itself; the stylesheet scales its background layer, so the
 * card on top stays where it is.
 *
 * @param {Object} slide Entry from SHOWCASE_SLIDES.
 * @return {Array} Inner-block template for the media half.
 */
function getMediaHalf(slide) {
  return [
    'core/group',
    { className: 'showcase-slide__media', templateLock: false },
    [
      [
        'core/cover',
        {
          url: slide.url,
          alt: slide.alt,
          dimRatio: 20,
          minHeight: 540,
          className: 'brandy-zoom-settle',
          layout: { type: 'constrained', contentSize: '280px' },
        },
        [
          [
            'core/group',
            {
              // Cover paints its content white and the card is white too, so
              // the text colour has to be said out loud or it disappears.
              style: {
                color: { text: '#1a1a1a', background: '#ffffff' },
                spacing: { padding: CARD_PADDING },
                border: { radius: '8px' },
              },
              layout: { type: 'constrained' },
            },
            [
              [
                'core/paragraph',
                {
                  content: slide.category,
                  fontSize: 'small',
                  style: {
                    typography: {
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontStyle: 'normal',
                      fontWeight: '600',
                    },
                  },
                },
              ],
              [
                'core/heading',
                { level: 3, content: slide.product, fontSize: 'medium' },
              ],
              [
                'core/paragraph',
                {
                  content: slide.price,
                  style: {
                    typography: { fontStyle: 'normal', fontWeight: '700' },
                  },
                },
              ],
            ],
          ],
        ],
      ],
    ],
  ];
}

/**
 * Panel half — what slides inside the stationary frame.
 *
 * No background of its own: the colour is the panel's, so that it can stay put
 * and cross-fade while this content slides through it.
 *
 * @param {Object} slide Entry from SHOWCASE_SLIDES.
 * @return {Array} Inner-block template for the panel half.
 */
function getPanelHalf(slide) {
  return [
    'core/group',
    { className: 'showcase-slide__panel', templateLock: false },
    [
      [
        'core/heading',
        { level: 2, content: slide.quote, fontSize: 'x-large' },
      ],
      [
        'core/paragraph',
        {
          content: slide.author,
          style: { typography: { fontStyle: 'normal', fontWeight: '700' } },
        },
      ],
      ['core/paragraph', { content: slide.location, fontSize: 'small' }],
    ],
  ];
}

/**
 * A slide's own template: the two halves, in render order.
 *
 * Module-level constant — the block editor re-syncs inner blocks whenever a
 * template's identity changes, so this must not be rebuilt per render.
 */
export const SHOWCASE_SLIDE_HALVES = [
  ['core/group', { className: 'showcase-slide__media', templateLock: false }],
  ['core/group', { className: 'showcase-slide__panel', templateLock: false }],
];

/**
 * The slider's starter slides, pre-filled.
 */
export const SHOWCASE_STARTER_SLIDES = SHOWCASE_SLIDES.map((slide) => [
  'brandy/showcase-slide',
  { panelColor: slide.panelColor },
  [getMediaHalf(slide), getPanelHalf(slide)],
]);
